import { Flex, Text } from '@chakra-ui/react'
import { addMetadata, useApp } from '@hb/react'
import {
  ASSESSMENTS_REF,
  ContactButtons,
  GenericEditModal,
  useFormsView,
  useUpdateDoc,
} from '@hb/react-web'
import { FieldMapValue, getMostRecentAssessment, getSignOnFields } from '@hb/shared'
import { doc } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { SignOnStagePanel } from './SignOnStagePanel'

const SignOnCompleteMessage = () => (
  <Flex pb={2} gap={1} px={3} w="100%" flexFlow="column">
    <Text pt={2}>
      You have completed the sign-on questionnaire. If any of this information changes, please
      contact us.
    </Text>
    <ContactButtons id="FormsView" noDivider />
  </Flex>
)

export const SignOnQuestionnaireStatus = () => {
  const update = useUpdateDoc('sign on data')
  const { appName } = useApp()
  const {
    assessmentId,
    assessments,
    assessment: { populated: assessment },
  } = useFormsView()
  const { signOnData, signedOnDate, plans } = assessment ?? {}
  const [viewSignOnQuestionnaire, setViewSignOnQuestionnaire] = useState(false)

  const hasPreviousSignOn = useMemo(
    () =>
      Object.keys(assessments ?? {})
        .filter(aId => aId !== assessmentId)
        .some(key => assessments?.[key].signedOnDate),
    [assessments, assessmentId],
  )

  const initialValue = useMemo(
    () =>
      signOnData ??
      (assessments
        ? getMostRecentAssessment(
            Object.entries(assessments).reduce((acc, [key, data]) => {
              if (data.signOnData) return { ...acc, [key]: data }
              return acc
            }, {}),
          )?.signOnData
        : null),
    [signOnData, assessments],
  )

  const signOnFields = useMemo(() => getSignOnFields(plans?.primary), [plans?.primary])

  const onSignOnDataSubmit = useCallback(
    (v: FieldMapValue) =>
      update(doc(ASSESSMENTS_REF, assessmentId), '', {
        signOnData: addMetadata(v, appName, !assessment?.signOnData),
        signedOnDate: signedOnDate || Date.now(),
      }),
    [appName, assessmentId, signedOnDate, assessment, update],
  )

  const buttonText = useMemo(() => {
    if (!signedOnDate) return 'Complete Form'
    return 'View Form'
  }, [signedOnDate])

  return (
    <>
      <SignOnStagePanel
        complete={!!signedOnDate}
        title="Sign-on Form"
        isFirst
        onOpen={() => {
          setViewSignOnQuestionnaire(true)
        }}
        buttonText={buttonText}
      />
      {viewSignOnQuestionnaire ? (
        <GenericEditModal
          adminView={false}
          canSubmitClean={hasPreviousSignOn}
          field={signOnFields}
          onSubmit={onSignOnDataSubmit}
          data={initialValue ?? {}}
          renderFooter={signedOnDate ? () => <SignOnCompleteMessage /> : undefined}
          footerProps={{
            padding: 0,
          }}
          readOnly={!!signedOnDate}
          isOpen
          onClose={() => {
            setViewSignOnQuestionnaire(false)
          }}
        />
      ) : null}
    </>
  )
}
